import React from 'react';
import { Pressable, StatusBar } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

type CoachMaterialHeaderProps = {
  title: string;
  onBack: () => void;
};

function PhoneCoachMaterialHeader({ title, onBack }: CoachMaterialHeaderProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack direction="row" align="center" style={styles.phoneHeader}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={STRINGS.guardianPlayer.levelDetail.back}
        hitSlop={scale(8)}
        onPress={onBack}
        style={({ pressed }) => [styles.phoneBackButton, pressed ? styles.pressed : null]}>
        <SvgIcon name="IcBackBlue" size={scale(24)} color={guardianColors['10']} />
      </Pressable>

      <Text
        variant="Font Guardian App/Heading/Heading H6 18px"
        numberOfLines={1}
        style={styles.phoneTitle}>
        {title}
      </Text>
    </Stack>
  );
}

function TabletCoachMaterialHeader({ title, onBack }: CoachMaterialHeaderProps) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack direction="row" align="center" style={styles.tabletHeader}>
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={STRINGS.guardianPlayer.levelDetail.back}
        hitSlop={scale(8)}
        onPress={onBack}
        style={({ pressed }) => [styles.tabletBackButton, pressed ? styles.pressed : null]}>
        <SvgIcon name="IcBackBlue" size={scale(24)} color={guardianColors['Main/Main']} />
        <Text variant="Font Inter/Subtitle/Medium 14px" style={styles.tabletBackText}>
          {STRINGS.guardianPlayer.levelDetail.back}
        </Text>
      </Pressable>

      <Text variant="Font Inter/Subtitle/Bold 14px" numberOfLines={1} style={styles.tabletTitle}>
        {title}
      </Text>

      <Stack width={scale(34)} height={scale(40)} />
    </Stack>
  );
}

export function CoachMaterialHeader(props: CoachMaterialHeaderProps) {
  const { isTablet, scale } = useScaling();
  const insets = useSafeAreaInsets();

  return (
    <>
      <StatusBar barStyle="dark-content" backgroundColor={colors['0']} />
      <Stack backgroundColor={colors['0']} style={{ paddingTop: insets.top }}>
        {isTablet ? (
          <TabletCoachMaterialHeader {...props} />
        ) : (
          <Stack height={scale(56)} paddingTop={scale(2)}>
            <PhoneCoachMaterialHeader {...props} />
          </Stack>
        )}
      </Stack>
    </>
  );
}

const useStyles = createStyles(({ scale }) => ({
  phoneHeader: {
    width: '100%',
  },
  phoneBackButton: {
    width: scale(40),
    height: scale(40),
    marginLeft: scale(20),
    borderRadius: scale(20),
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: guardianColors['1'],
  },
  phoneTitle: {
    flex: 1,
    marginLeft: scale(10),
    marginRight: scale(20),
    color: guardianColors['10'],
  },
  tabletHeader: {
    width: '100%',
    height: scale(60),
    gap: scale(30),
    paddingHorizontal: scale(16),
    paddingVertical: scale(10),
    borderBottomWidth: 1,
    borderBottomColor: guardianColors['3'],
  },
  tabletBackButton: {
    height: scale(40),
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: scale(10),
  },
  tabletBackText: {
    color: guardianColors['Main/Main'],
  },
  tabletTitle: {
    flex: 1,
    color: guardianColors['Main/Main'],
    textAlign: 'center',
  },
  pressed: {
    opacity: 0.72,
  },
}));
